import type { Experiment } from 'types';
import type { StageCollisions, StageDraft } from '@/state/pipelines/types';
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PIPELINE_ADVANCE_RULES } from 'definitions';
import { ChevronLeft, ChevronRight, Lock, Trash2 } from 'lucide-react';
import { cn } from 'helpers/cn';
import { ExperimentCard } from './ExperimentCard';
import { CollisionNotice } from './CollisionNotice';
import { ADVANCE_RULE_HINTS, ADVANCE_RULE_LABELS, STAGE_STATE_TONES } from './labels';

type StageColumnProps = {
  stage: StageDraft;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  /** Puts the product tour's card anchor on this column's first experiment. */
  anchorFirstCard: boolean;
  experiments: Experiment[];
  collisions: StageCollisions | null;
  onRename: (name: string) => void;
  onAdvanceRuleChange: (rule: StageDraft['advanceRule']) => void;
  onMove: (direction: -1 | 1) => void;
  onDelete: () => void;
  onDropExperiment: (experimentId: number) => void;
  onRemoveExperiment: (experimentId: number) => void;
};

const ICON_BUTTON =
  'text-dim hover:text-foreground hover:bg-hover rounded-lg p-1 transition-colors duration-150 disabled:opacity-30';

export const StageColumn = ({
  stage,
  index,
  isFirst,
  isLast,
  anchorFirstCard,
  experiments,
  collisions,
  onRename,
  onAdvanceRuleChange,
  onMove,
  onDelete,
  onDropExperiment,
  onRemoveExperiment,
}: StageColumnProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const frozen = stage.state !== 'pending';

  // Which cards to outline: an experiment named in any reportable pair for this stage.
  const flagged = new Map<number, 'warn' | 'block'>();
  for (const pair of collisions?.pairs ?? []) {
    const verdict = pair.collision.verdict === 'block' ? 'block' : 'warn';
    for (const id of [pair.aId, pair.bId]) {
      if (flagged.get(id) !== 'block') flagged.set(id, verdict);
    }
  }

  const handleDragOver = (ev: React.DragEvent) => {
    if (frozen) return;
    ev.preventDefault();
    ev.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (ev: React.DragEvent) => {
    if (!ev.currentTarget.contains(ev.relatedTarget as HTMLElement)) setIsDragOver(false);
  };

  const handleDrop = (ev: React.DragEvent) => {
    ev.preventDefault();
    setIsDragOver(false);
    if (frozen) return;
    const experimentId = Number(ev.dataTransfer.getData('experimentId'));
    if (Number.isInteger(experimentId) && experimentId > 0) onDropExperiment(experimentId);
  };

  return (
    <div
      data-tour={isFirst ? 'pipelines-stage' : undefined}
      onDragOver={handleDragOver}
      onDragEnter={() => !frozen && setIsDragOver(true)}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'border-line bg-panel rounded-panel flex w-[270px] shrink-0 flex-col gap-2.5 border p-2.5 transition-colors duration-150',
        // The stage that is live now is the one thing on the board worth finding at a glance.
        stage.state === 'running' && 'border-ok-line',
        isDragOver && 'border-sel-line bg-sel-bg',
        collisions?.worst === 'block' && 'border-danger-line',
      )}
    >
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <span className="micro-label shrink-0 tracking-normal">{index + 1}</span>
          {frozen ? (
            <p className="flex-1 truncate text-body font-medium" title={stage.name}>
              {stage.name}
            </p>
          ) : (
            <Input
              value={stage.name}
              onChange={(ev) => onRename(ev.target.value)}
              className="hover:border-line focus:border-line h-7 flex-1 rounded-lg border-transparent bg-transparent px-1.5 text-body font-medium"
              aria-label={`Stage ${index + 1} name`}
            />
          )}
          {frozen && <Lock className="text-dim size-3.5 shrink-0" strokeWidth={1.5} />}
        </div>

        <div className="flex items-center gap-1">
          <span className={cn('micro-label rounded-pill border px-2 py-0.5', STAGE_STATE_TONES[stage.state])}>
            {stage.state}
          </span>
          <div className="ml-auto flex items-center gap-0.5">
            <button
              type="button"
              disabled={isFirst || frozen}
              onClick={() => onMove(-1)}
              className={ICON_BUTTON}
              aria-label={`Move stage ${index + 1} earlier`}
            >
              <ChevronLeft className="size-3.5" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              disabled={isLast || frozen}
              onClick={() => onMove(1)}
              className={ICON_BUTTON}
              aria-label={`Move stage ${index + 1} later`}
            >
              <ChevronRight className="size-3.5" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              disabled={frozen}
              onClick={onDelete}
              className={ICON_BUTTON}
              aria-label={`Delete stage ${index + 1}`}
            >
              <Trash2 className="size-3.5" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Changing this on a running stage is allowed: it decides when the stage ends,
            and switching to manual mid-flight is a legitimate thing to want. */}
        <Select
          value={stage.advanceRule}
          onValueChange={(value) => onAdvanceRuleChange(value as StageDraft['advanceRule'])}
        >
          <SelectTrigger
            className="border-line bg-field h-7 rounded-lg text-meta"
            data-tour={isFirst ? 'pipelines-advance-rule' : undefined}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PIPELINE_ADVANCE_RULES.map((rule) => (
              <SelectItem key={rule} value={rule} className="text-meta">
                {ADVANCE_RULE_LABELS[rule]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="supporting">{ADVANCE_RULE_HINTS[stage.advanceRule]}</p>
      </div>

      <CollisionNotice collisions={collisions} />

      <div className="flex min-h-[80px] flex-col gap-2">
        {experiments.length === 0 ? (
          <p className="supporting border-line rounded-card border border-dashed p-3 text-center">
            {frozen ? 'Nothing ran in this stage.' : 'Drag a draft experiment here.'}
          </p>
        ) : (
          experiments.map((experiment, idx) => (
            <ExperimentCard
              key={experiment.id}
              experiment={experiment}
              tourAnchor={anchorFirstCard && idx === 0 ? 'pipelines-card' : undefined}
              frozen={frozen}
              flagged={flagged.get(experiment.id) ?? null}
              onRemove={() => onRemoveExperiment(experiment.id)}
              onDragStart={(ev) => {
                ev.dataTransfer.setData('experimentId', String(experiment.id));
                ev.dataTransfer.effectAllowed = 'move';
              }}
            />
          ))
        )}
      </div>
    </div>
  );
};
